/**
 * The Retry of work on the person's own computer: the start of the server that comes before every
 * local Retry, and the toasts it shows.
 */
import { toast } from 'sonner';

import { RETRY_TOAST_MS } from '../request-error';
import { retryLocalAgent } from './connection';

/** What every local Retry says while the plugin starts the server on this computer again. */
export const LOCAL_AGENT_STARTING = 'Starting the agent on this computer…';

/**
 * The work waiting on each toast's start, by the key its caller gave it: a second ask for the same
 * work while that start runs, as a double click on a failed turn's Retry, is the same ask and runs
 * once. Other work asked for under the same toast while it runs, as another failed turn's Retry,
 * waits on the same start: it all runs once the server answers, and the toast that says the start
 * failed has one Retry, which asks for all of it again.
 */
const waiting = new Map<string, Map<string, () => unknown>>();

/**
 * What every local Retry does: asks the plugin to start the server on this computer again,
 * then runs `next` against the address it hands the page. A start that fails says why in a
 * toast of its own, with Retry, and `next` is skipped: it would only start the server a second
 * time to meet the same failure.
 *
 * The toast says the start is under way and stays on screen until the start answers, so its
 * answer replaces it in place. Sonner takes 200 ms to remove a toast whose action was pressed,
 * and replacing a toast in that time lost it or froze the tab, so its own Retry keeps it.
 *
 * `workKey` names the work: an ask for work that already waits on the start does nothing, and an
 * ask for other work under the same toast joins the start under way.
 */
export function startAgainThen(toastId: string, next: () => unknown, workKey = toastId): void {
  const joined = waiting.get(toastId);
  if (joined !== undefined) {
    if (!joined.has(workKey)) {
      joined.set(workKey, next);
    }
    return;
  }
  const work = new Map([[workKey, next]]);
  waiting.set(toastId, work);
  // An update keeps what it does not name, so the Retry of the toast it replaces goes explicitly.
  // Sonner restarts a toast's countdown only when its duration changes: Infinity here, so the
  // failure that replaces this, at RETRY_TOAST_MS, gets its full time on screen.
  toast.loading(LOCAL_AGENT_STARTING, {
    id: toastId,
    action: undefined,
    duration: Infinity,
  });
  void retryLocalAgent().then(
    () => {
      waiting.delete(toastId);
      // Sonner draws a toast on a timer but takes a dismissal through two animation frames, which
      // a busy page can run before that timer: a start that answers in the tick that showed the
      // toast would send a dismissal that comes before the toast and is lost, leaving the toast
      // on screen. A timer set now runs after the one that draws it.
      setTimeout(() => toast.dismiss(toastId));
      for (const run of work.values()) {
        run();
      }
    },
    (failure: unknown) => {
      waiting.delete(toastId);
      toast.error(failure instanceof Error ? failure.message : String(failure), {
        id: toastId,
        duration: RETRY_TOAST_MS,
        action: {
          label: 'Retry',
          onClick: (event) => {
            event.preventDefault();
            for (const [key, run] of work) {
              startAgainThen(toastId, run, key);
            }
          },
        },
      });
    },
  );
}

/**
 * The Retry of one session's failed work, which `next` does again: the plugin starts the server
 * again first, with the toasts every local Retry shows (`startAgainThen`), and a start already
 * under way, as the page's own after a crash, is joined: `next` runs once the server answers, and
 * not at all when the start fails, whose toast has a Retry of its own. The same work asked for again
 * while it waits, named by `workKey` such as a turn's id, runs once.
 */
export function startAgainFirst(sessionId: string, next: () => unknown, workKey?: string): void {
  const toastId = `code-session-local-retry:${sessionId}`;
  startAgainThen(toastId, next, workKey === undefined ? toastId : `${toastId}:${workKey}`);
}
